import React from 'react';
import { useDispatch, shallowEqual, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import {
  Button,
  Col,
  Modal,
  ModalProps,
  Row,
  Space,
} from 'antd';
import { CloseOutlined, CloudDownloadOutlined, PrinterOutlined, ReloadOutlined } from '@ant-design/icons';
import { printShipmentGroup, setPrintShipmentGroupResult } from 'actions/printActions';
import PrinterSelector from 'components/Sales/ScanToShip/PrinterSelector';
import {
  InfoButton,
  Label,
  Loading,
  ScreenMask,
  SiteContent,
  message,
} from 'components/common';
import {
  COMMON_ERR_MSG_DEFAULT_DISPLAY_DURATION,
  COMMON_MSG_DEFAULT_DISPLAY_DURATION,
  LOADING_ICON_SIZE1,
} from 'constants/config';
import { loadPrinterList } from 'middlewares/lazyCacheMiddleware';
import { RootState } from 'reducers';
import { exportShipmentGroupManifestToPdf } from 'services/shipment';
import { userProfiles } from 'utils/auth';
import ShippingAccountSelector from './ShippingAccountSelector';

type Props = ModalProps & {
  shipmentGroup: StringKAnyVPair;
};

const PrintShipmentGroupDialog = (props: Props) => {
  const { useState } = React;
  const [isPrinting, setIsPrinting] = useState(false);
  const [lockScreen, setLockScreen] = useState(false);
  const dispatch: Dispatch<any> = useDispatch();
  const [selectedPrinter, setSelectedPrinter] = useState<StringKAnyVPair>({});
  const [selectedShipAccount, setSelectedShipAccount] = useState<StringKAnyVPair>({});

  const printShipmentGroupResult: StringKAnyVPair = useSelector(
    (state: RootState) => state.sales.printShipmentGroupResult,
    shallowEqual
  );

  const dialogFooter = () => {
    return (
      <Row justify="space-between">
        <Button onClick={props.onCancel}>
          <CloseOutlined />
          Cancel
        </Button>
        <Space>
          <InfoButton
            disabled={!isExportEnabled()}
            onClick={exportShipmentGroupPdf}
          >
            <CloudDownloadOutlined />
            Export
          </InfoButton>
          <Button
            disabled={!isPrinterEnabled()}
            onClick={handlePrintShipmentGroup}
            type="primary"
          >
            <PrinterOutlined />
            Print
          </Button>
        </Space>
      </Row>
    );
  };

  const exportShipmentGroupPdf = async () => {
    const { code: sgCode, shipmentGroupNum, warehouseNum } = props.shipmentGroup;

    //console.log('p', sgCode, props.shipmentGroup)
    setLockScreen(true);
    try {
      const content = await exportShipmentGroupManifestToPdf(warehouseNum, shipmentGroupNum, selectedShipAccount.shipAccountNum);

      if (content) {
        //console.log('pdf', typeof content, content);
        //const fileType = headers['content-type'];
        const fileType = 'application/pdf';
        const blob = new Blob([content], { type: fileType });
        const fileName = fileType === 'application/pdf'
          ? `${sgCode}.pdf`
          : `${sgCode}.png`;

        if ('download' in document.createElement('a')) {
          const elink = document.createElement('a');

          elink.download = fileName;
          elink.style.display = 'none';
          elink.href = URL.createObjectURL(blob);
          document.body.appendChild(elink);
          elink.click();
          URL.revokeObjectURL(elink.href);
          document.body.removeChild(elink);
          setTimeout(() => {
            // @ts-ignore
            props.onCancel && props.onCancel();
          }, 0);
        }
      }
    } catch(e) {
      console.log('exported error', e);
    } finally {
      setLockScreen(false);
    }
  };

  const handlePrintShipmentGroup = () => {
    const { profile } = userProfiles;
    const pd = {
      MasterAccountNum: profile.masterAccountNum,
      ProfileNum: profile.profileNum,
      WarehouseNum: props.shipmentGroup.warehouseNum,
      ShipmentGroupNum: props.shipmentGroup.shipmentGroupNum,
      ShipAccountNum: selectedShipAccount.shipAccountNum,
      PrinterAccountNum: selectedPrinter.printerAccountNum,
      PrinterNum: selectedPrinter.printerNum,
      User: profile.email,
    };

    //console.log(pd);
    dispatch(printShipmentGroup(pd));
    setIsPrinting(true);
  };

  const isExportEnabled = () => {
    return selectedShipAccount.shipAccountNum > 0;
  };

  const isPrinterEnabled = () => {
    //return (!isPrinting) && selectedShipAccount.shipAccountNum > 0;
    return (!isPrinting) && selectedShipAccount.shipAccountNum > 0 && selectedPrinter.printerAccountNum > 0;
  };

  const onSelectPrinter = (p: StringKAnyVPair) => {
    setSelectedPrinter(p);
  };

  const onSelectShippingAccount = (sa: StringKAnyVPair) => {
    setSelectedShipAccount(sa || {});
  };

  const refreshPrinterList = async () => {
    setLockScreen(true);
    try {
      await loadPrinterList(dispatch);
    } catch(e) {
      message.error({
        content: `Loaded printer list error: ${e}`,
        duration: COMMON_ERR_MSG_DEFAULT_DISPLAY_DURATION,
      });
    } finally {
      setLockScreen(false);
    }
  };

  React.useEffect(() => {
    if (Object.keys(printShipmentGroupResult).length > 0) {
      const { code, data } = printShipmentGroupResult;
      const msg = typeof data === 'string' && data ? data : 'No message from printing worker';
      //console.log('prs', printShipmentGroupResult);
      setIsPrinting(false);
      dispatch(setPrintShipmentGroupResult({}));

      if (code === 200) {
        message.success({
          content: msg,
          duration: COMMON_MSG_DEFAULT_DISPLAY_DURATION,
        });
        setTimeout(() => {
          // @ts-ignore
          props.onCancel && props.onCancel();
        }, 0);
      } else {
        console.log('prse', printShipmentGroupResult);
        message.error({
          content: msg,
          duration: COMMON_ERR_MSG_DEFAULT_DISPLAY_DURATION,
        });
      }
    }
  }, [dispatch, printShipmentGroupResult, props]);

  return (<>
    <Modal
      {...props}
      footer={dialogFooter()}
    >
      <SiteContent className="common-content">
        <Row align="middle">
          <Col className="text-end" span={8}>
            <Label className="label-bold tail-colon2">Shipping Account</Label>
          </Col>
          <Col span={14}>
            <ShippingAccountSelector
              onChange={onSelectShippingAccount}
              shipmentGroupNum={props.shipmentGroup.shipmentGroupNum}
              warehouseNum={props.shipmentGroup.warehouseNum}
            />
          </Col>
        </Row>
        <Row align="middle" className="mt-8">
          <Col className="text-end" span={8}>
            <Label className="label-bold tail-colon2">Printer</Label>
          </Col>
          <Col span={14}>
            <PrinterSelector
              onChange={onSelectPrinter}
              placeholder="Please select a printer"
              //value={}
            />
          </Col>
          <Col>
            <Button
              className="ml-4 p-2-8"
              onClick={refreshPrinterList}
            >
              <ReloadOutlined />
            </Button>
          </Col>
        </Row>
      </SiteContent>
    </Modal>
    {lockScreen && (
      <ScreenMask>
        <Loading size={LOADING_ICON_SIZE1} />
      </ScreenMask>
    )}
  </>);
};

export default PrintShipmentGroupDialog;
